import { Building2, MapPin } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { worksAtAssignedVenues } from '@/auth/access'
import { isInVenueSupportMode } from '@/auth/roles'
import { useSwitchableVenues } from '@/hooks/useSwitchableVenues'
import type { User } from '@/schemas/auth.schema'
import { objectPositionOf } from '@lib/helpers/focal-point'
import { cn } from '@/lib/utils'

/**
 * A quiet strip naming the venue the session is working in, on every page
 * inside a venue.
 *
 * The sidebar's scope card is the control for moving between venues; this is
 * the answer to "which venue am I changing?", which needs answering constantly
 * — and on a phone, where the sidebar is folded away, it is the only answer on
 * screen. Getting it wrong is expensive in this app: an inventory edit or a
 * floor plan saved against the wrong venue is real work done to the wrong place.
 *
 * The NAME identifies the venue, so it is the one strong thing here. The venue's
 * photograph sits beside it as a framed thumbnail — evidence, not wallpaper, per
 * Plan View — and never under the text. A venue with no photograph shows the
 * empty well, not a stock picture that could be read as its building.
 *
 * The venue comes from the switchable-venues query the sidebar has already
 * fetched, so this costs no extra request.
 */
/**
 * Whether the band is on screen for this session.
 *
 * Exported so a page can tell: the venue dashboard has its own hero for an
 * organization admin browsing from outside, and must not draw it underneath this
 * one for someone working inside the venue.
 */
export function showsVenueContextHero(user: User | null | undefined): boolean {
  if (!worksAtAssignedVenues(user) && !isInVenueSupportMode(user)) return false
  if (!isInVenueSupportMode(user)) return false
  return user.supportVenue.venueId !== '' && user.supportVenue.venueName !== ''
}

export function VenueContextHero() {
  const { user } = useAuth()
  const venuesQuery = useSwitchableVenues()

  if (!showsVenueContextHero(user) || !isInVenueSupportMode(user)) return null
  const venueId = user.supportVenue.venueId
  const name = user.supportVenue.venueName

  const venue = venuesQuery.data?.venues.find((v) => v.id === venueId) ?? null
  const cover = venue?.coverImageUrl ?? null

  return (
    <div className="flex items-center gap-3 border-b border-line bg-surface px-6 py-2.5 md:px-8">
      <span
        aria-hidden
        className={cn(
          'relative grid h-8 w-[42px] shrink-0 place-items-center overflow-hidden rounded-sm bg-surface-sunken text-ink-subtle',
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-sm after:shadow-[var(--ds-media-ring)]',
          cover ? undefined : 'plan-grid-fine',
        )}
      >
        {cover ? (
          <img
            src={cover}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: objectPositionOf(venue?.coverFocalPoint ?? null, '50% 45%') }}
          />
        ) : (
          <Building2 className="h-4 w-4" />
        )}
      </span>
      <div className="min-w-0">
        <p className="font-eyebrow m-0 leading-4">Working in</p>
        <p className="m-0 truncate text-heading font-semibold leading-5 text-ink">{name}</p>
      </div>
      {venue?.isPrimary ? (
        <span className="ml-auto hidden items-center gap-1 text-xs text-ink-muted sm:flex">
          <MapPin className="h-3 w-3" aria-hidden /> Your home venue
        </span>
      ) : null}
    </div>
  )
}
